import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DeliverableImportTypes } from '@mono/proto-clients';
import { Testids } from '@mono/ui-selectors';
import { useNavigate } from 'react-router-dom';
import { useChatFeedback } from '../../ChatFeedbackContext';
import { deliverableImportFeedback } from './card';
import { PROGRAM_LEVEL_KEY, workstreamLabel } from './shared';
const { DeliverableImportAction, DeliverableImportDecision, DeliverableCommitStatus } = DeliverableImportTypes;
/**
 * Buckets proposals into RECORDED outcomes, never predicted ones: a
 * COMMITTED decision is required before something counts as created/updated/
 * matched, and "verified" reflects commit_result.verified rather than the
 * fact a commit was attempted. SUPERSEDED proposals are excluded — they were
 * replaced by a later revision and carry no outcome of their own.
 */ function summarizeOutcomes(proposals) {
    let created = 0;
    let updated = 0;
    let alreadyMatches = 0;
    let rejected = 0;
    let pending = 0;
    let failed = 0;
    let pendingDecision = 0;
    let unresolvedClarification = 0;
    let uncommittedApproved = 0;
    let unverified = 0;
    for (const p of proposals){
        if (p.decision === DeliverableImportDecision.SUPERSEDED) continue;
        if (p.decision === DeliverableImportDecision.REJECTED) {
            rejected++;
            continue;
        }
        if (p.decision === DeliverableImportDecision.COMMIT_FAILED) {
            failed++;
            continue;
        }
        if (p.decision === DeliverableImportDecision.COMMITTED) {
            const verified = p.commitResult?.verified === true;
            if (p.action === DeliverableImportAction.ALREADY_MATCHES) {
                alreadyMatches++;
            } else if (p.commitResult?.status === DeliverableCommitStatus.CREATED) {
                created++;
            } else {
                updated++;
            }
            if (!verified) unverified++;
            continue;
        }
        // PENDING, APPROVED, EDITED_APPROVED: decided-or-not, nothing has actually
        // been written yet.
        pending++;
        if (p.decision === DeliverableImportDecision.PENDING) {
            pendingDecision++;
            if (p.action === DeliverableImportAction.NEEDS_CLARIFICATION) unresolvedClarification++;
        } else {
            uncommittedApproved++;
        }
    }
    const committedTotal = created + updated + alreadyMatches;
    return {
        created,
        updated,
        alreadyMatches,
        rejected,
        pending,
        failed,
        pendingDecision,
        unresolvedClarification,
        uncommittedApproved,
        unverified,
        committedTotal,
        verifiedTotal: committedTotal - unverified
    };
}
/**
 * Shared body for PAUSED_RECAP and IMPORT_COMPLETE: both are whole-import
 * recaps (every workstream, not one) built from RECORDED outcomes, per
 * SAD-660. They differ only in wording/emphasis — PAUSED_RECAP always says
 * so explicitly, and IMPORT_COMPLETE only claims unqualified success when
 * nothing is pending, uncommitted, failed, or unverified.
 */ export function ImportRecapCard({ mode, payload, review }) {
    const navigate = useNavigate();
    const sendFeedback = useChatFeedback();
    const counts = summarizeOutcomes(review.proposals);
    const isClean = counts.pending === 0 && counts.failed === 0 && counts.unverified === 0;
    const outstandingCount = counts.pending + counts.failed + counts.unverified;
    const workstreamRows = review.workstreams.map((ws)=>({
            ws,
            counts: summarizeOutcomes(review.proposals.filter((p)=>p.workstreamKey === ws.key))
        }));
    const workstreamsReviewed = review.workstreams.filter((ws)=>ws.key !== PROGRAM_LEVEL_KEY).length;
    const totals = review.totals;
    const verificationLine = (()=>{
        if (counts.committedTotal === 0) {
            return mode === 'paused' ? 'No changes have been committed yet.' : 'No changes were committed in this import.';
        }
        if (mode === 'complete' && isClean) {
            return 'All committed changes were read back and verified against their approved proposals.';
        }
        const suffix = mode === 'paused' ? 'so far' : 'in this import';
        return `${counts.verifiedTotal} of ${counts.committedTotal} committed changes were read back and verified ${suffix}.`;
    })();
    const handleViewTasks = ()=>{
        navigate(`/delivery/programs/${payload.programId}/deliverables`);
        sendFeedback(deliverableImportFeedback({
            event: 'navigate',
            changeSetId: payload.changeSetId,
            text: 'Program Lead opened the Deliverables page to see the imported tasks.'
        }));
    };
    const handleReviewUnresolved = ()=>{
        sendFeedback(deliverableImportFeedback({
            event: 'navigate',
            changeSetId: payload.changeSetId,
            text: `Program Lead wants to review the ${counts.pendingDecision} unresolved item${counts.pendingDecision === 1 ? '' : 's'} still needing a decision.`
        }));
    };
    const handleAddEvidence = ()=>{
        sendFeedback(deliverableImportFeedback({
            event: 'navigate',
            changeSetId: payload.changeSetId,
            text: 'Program Lead wants help capturing deliverable evidence.'
        }));
    };
    const statBoxes = [
        {
            label: 'Created',
            value: counts.created
        },
        {
            label: 'Updated',
            value: counts.updated
        },
        {
            label: 'Already Matched',
            value: counts.alreadyMatches
        },
        {
            label: 'Rejected',
            value: counts.rejected
        },
        {
            label: 'Pending / Unresolved',
            value: counts.pending
        },
        {
            label: 'Failed',
            value: counts.failed,
            danger: counts.failed > 0
        }
    ];
    return /*#__PURE__*/ _jsxs("div", {
        className: "mt-2 space-y-3 text-sm text-gray-700",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "font-medium text-gray-900",
                        children: review.fileName
                    }),
                    review.sheetName && /*#__PURE__*/ _jsxs("span", {
                        className: "text-gray-500",
                        children: [
                            ' · sheet "',
                            review.sheetName,
                            '"'
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "text-xs text-gray-600",
                children: [
                    workstreamsReviewed,
                    " workstream",
                    workstreamsReviewed === 1 ? '' : 's',
                    " reviewed —",
                    ' ',
                    totals?.workbookRows ?? 0,
                    " workbook rows processed (",
                    totals?.deliverableRecords ?? 0,
                    ' ',
                    "deliverable records, ",
                    totals?.scheduledOccurrences ?? 0,
                    " scheduled occurrences)."
                ]
            }),
            mode === 'paused' ? /*#__PURE__*/ _jsx("div", {
                className: "font-medium text-gray-900",
                children: "Paused — nothing else has been changed."
            }) : isClean ? /*#__PURE__*/ _jsx("div", {
                className: "font-medium text-green-700",
                children: "All done — every deliverable in this import was resolved and every commit verified."
            }) : /*#__PURE__*/ _jsxs("div", {
                className: "font-medium text-amber-700",
                children: [
                    "Imported, with ",
                    outstandingCount,
                    " item",
                    outstandingCount === 1 ? '' : 's',
                    " still needing your attention."
                ]
            }),
            mode === 'paused' && /*#__PURE__*/ _jsxs("div", {
                className: "text-xs text-gray-700",
                children: [
                    counts.committedTotal,
                    " deliverable",
                    counts.committedTotal === 1 ? '' : 's',
                    " committed so far.",
                    ' ',
                    counts.pendingDecision > 0 ? `${counts.pendingDecision} still pending your review${counts.unresolvedClarification > 0 ? ` (${counts.unresolvedClarification} needing clarification)` : ''}.` : 'Nothing is currently pending your review.',
                    counts.uncommittedApproved > 0 && ` ${counts.uncommittedApproved} approved but not yet committed — nothing will be written until you commit that workstream.`
                ]
            }),
            mode === 'complete' && !isClean && /*#__PURE__*/ _jsxs("ul", {
                className: "text-xs text-gray-700 list-disc pl-4 space-y-0.5",
                children: [
                    counts.pendingDecision > 0 && /*#__PURE__*/ _jsxs("li", {
                        children: [
                            counts.pendingDecision,
                            " still pending review",
                            counts.unresolvedClarification > 0 ? ` (${counts.unresolvedClarification} needing clarification)` : ''
                        ]
                    }),
                    counts.uncommittedApproved > 0 && /*#__PURE__*/ _jsxs("li", {
                        children: [
                            counts.uncommittedApproved,
                            " approved but not yet committed"
                        ]
                    }),
                    counts.failed > 0 && /*#__PURE__*/ _jsxs("li", {
                        children: [
                            counts.failed,
                            " failed to commit"
                        ]
                    }),
                    counts.unverified > 0 && /*#__PURE__*/ _jsxs("li", {
                        children: [
                            counts.unverified,
                            " committed but not verified"
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "grid grid-cols-3 gap-2 text-xs",
                children: statBoxes.map((box)=>/*#__PURE__*/ _jsxs("div", {
                        className: `border rounded-md p-2 ${box.danger ? 'bg-red-50' : 'bg-gray-50'}`,
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-gray-500",
                                children: box.label
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: `text-base font-semibold ${box.danger ? 'text-red-700' : 'text-gray-900'}`,
                                children: box.value
                            })
                        ]
                    }, box.label))
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "text-xs text-gray-600",
                children: verificationLine
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "text-xs text-gray-600",
                children: "Unapproved, rejected, and unresolved proposals were not applied."
            }),
            mode === 'complete' && isClean && /*#__PURE__*/ _jsx("div", {
                className: "text-xs text-gray-600",
                children: "The approved and committed deliverables are now populated in the Tasks section."
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "overflow-auto max-h-64 border rounded-md",
                children: /*#__PURE__*/ _jsxs("table", {
                    className: "w-full text-xs",
                    children: [
                        /*#__PURE__*/ _jsx("thead", {
                            className: "sticky top-0 bg-gray-50 text-gray-500",
                            children: /*#__PURE__*/ _jsxs("tr", {
                                children: [
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "text-left font-medium px-2 py-1",
                                        children: "Workstream"
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "text-right font-medium px-2 py-1",
                                        children: "Created"
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "text-right font-medium px-2 py-1",
                                        children: "Updated"
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "text-right font-medium px-2 py-1",
                                        children: "Matched"
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "text-right font-medium px-2 py-1",
                                        children: "Rejected"
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "text-right font-medium px-2 py-1",
                                        children: "Pending"
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "text-right font-medium px-2 py-1",
                                        children: "Failed"
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx("tbody", {
                            children: workstreamRows.map(({ ws, counts: wc })=>/*#__PURE__*/ _jsxs("tr", {
                                    "data-testid": Testids.deliverableImport.workstreamRow,
                                    className: "border-t",
                                    children: [
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-2 py-1 text-gray-900",
                                            children: workstreamLabel(ws)
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-2 py-1 text-right",
                                            children: wc.created
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-2 py-1 text-right",
                                            children: wc.updated
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-2 py-1 text-right",
                                            children: wc.alreadyMatches
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-2 py-1 text-right",
                                            children: wc.rejected
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-2 py-1 text-right",
                                            children: wc.pending
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-2 py-1 text-right",
                                            children: wc.failed
                                        })
                                    ]
                                }, ws.key))
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center gap-3",
                children: [
                    /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        "data-testid": Testids.deliverableImport.viewTasks,
                        onClick: handleViewTasks,
                        className: "inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors",
                        children: "View Tasks"
                    }),
                    counts.pendingDecision > 0 && /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        "data-testid": Testids.deliverableImport.reviewUnresolved,
                        onClick: handleReviewUnresolved,
                        className: "text-xs font-medium text-gray-600 hover:text-gray-800",
                        children: "Review Unresolved Items"
                    }),
                    /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        "data-testid": Testids.deliverableImport.addEvidence,
                        onClick: handleAddEvidence,
                        className: "text-xs font-medium text-gray-600 hover:text-gray-800",
                        children: "Add Deliverable Evidence"
                    })
                ]
            })
        ]
    });
}
